import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform, Pressable, ScrollView, Switch } from 'react-native';
import { ON_ACCENT_TEXT } from '../styles/accentText';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors } from '../styles/colors';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { loadCookieConsent, peekCookieConsent, setCookieConsent } from '../services/cookieConsent';
import {
  ALL_COOKIES,
  NECESSARY_COOKIES_ONLY,
  type CookieCategory,
  type CookieChoice,
} from '../services/cookieChoice';
import { marketingTags } from '../services/marketingTagRuntime';
import { TAG_CATEGORY, type TagCategory } from '../services/marketingTags';
import { navigateTo } from '../navigation/rootNavigation';
import { closeCookiePreferences, useCookiePreferencesOpen } from './cookiePreferences';

import { surfaceFill } from '../styles/platformBackgrounds';
/**
 * CORE-1659 — окно «Настройки cookie»: что входит в каждую категорию и выбор по
 * двум необязательным — аналитическим и маркетинговым.
 *
 * Перечень счётчиков берётся из того, что реально настроено для этого
 * посетителя (`marketingTags.availableProviders`), а не зашит в текст: названная
 * в окне площадка, которой на странице нет, — такая же неправда, как
 * неназванная, которая есть.
 *
 * Окно ничего не сохраняет, пока человек не нажал одну из трёх кнопок: закрытие
 * крестиком или по фону оставляет прежний выбор (или его отсутствие — тогда
 * баннер продолжает спрашивать).
 */
export const CookiePreferencesModal: React.FC = () => {
  const visible = useCookiePreferencesOpen();
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useI18n('common');
  const [draft, setDraft] = useState<CookieChoice>(NECESSARY_COOKIES_ONLY);

  // Каждое открытие начинается с действующего выбора; без ответа — всё выключено:
  // согласие даётся действием, а не оставленной по умолчанию галочкой.
  //
  // Выбор читается из стораджа здесь же, а не берётся снимком: у гостя на хосте
  // без счётчиков его до этого не читает никто — баннер и счётчики молчат, синк
  // с сервером ждёт входа. Снимок после перезагрузки пуст, и окно показывало
  // сохранённое «принять все» выключенным (FAIL на стейджинге 23.10).
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setDraft(peekCookieConsent() ?? NECESSARY_COOKIES_ONLY);
    void loadCookieConsent().then((choice) => {
      if (!cancelled) setDraft(choice ?? NECESSARY_COOKIES_ONLY);
    });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (Platform.OS !== 'web' || !visible) return null;

  const save = (choice: CookieChoice) => {
    void setCookieConsent(choice);
    closeCookiePreferences();
  };

  const providerNames = (category: TagCategory): string =>
    marketingTags
      .availableProviders()
      .filter((p) => TAG_CATEGORY[p.kind] === category)
      .map((p) => t(`cookies.providers.${p.kind}`))
      .join(', ');

  const openPolicy = () => {
    closeCookiePreferences();
    navigateTo('LegalDoc', { docId: 'privacy-policy' });
  };

  const fixedRow = (key: 'necessary' | 'functional') => {
    // CORE-1775 — в необходимых есть и сторонний счётчик (Метрика): называем его.
    const names = key === 'necessary' ? providerNames('necessary') : '';
    return (
      <View style={[styles.category, { borderColor: themeColors.border }]}>
        <View style={styles.categoryHead}>
          <Text style={[styles.categoryTitle, { color: themeColors.textPrimary }]}>{t(`cookies.${key}.title`)}</Text>
          <Text style={[styles.alwaysOn, { color: themeColors.textSecondary }]}>{t('cookies.alwaysOn')}</Text>
        </View>
        <Text style={[styles.categoryDesc, { color: themeColors.textSecondary }]}>{t(`cookies.${key}.description`)}</Text>
        {names ? (
          <Text style={[styles.providers, { color: themeColors.textSecondary }]}>{t('cookies.providersList', { list: names })}</Text>
        ) : null}
      </View>
    );
  };

  const choiceRow = (category: CookieCategory) => {
    const names = providerNames(category);
    return (
      <View style={[styles.category, { borderColor: themeColors.border }]}>
        <View style={styles.categoryHead}>
          <Text style={[styles.categoryTitle, { color: themeColors.textPrimary }]}>{t(`cookies.${category}.title`)}</Text>
          <Switch
            testID={`cookie-category-${category}`}
            accessibilityLabel={t(`cookies.${category}.title`)}
            value={draft[category]}
            onValueChange={(value) => setDraft((prev) => ({ ...prev, [category]: value }))}
            trackColor={{ false: themeColors.border, true: colors.orange600 }}
          />
        </View>
        <Text style={[styles.categoryDesc, { color: themeColors.textSecondary }]}>{t(`cookies.${category}.description`)}</Text>
        <Text style={[styles.providers, { color: themeColors.textSecondary }]}>
          {names ? t('cookies.providersList', { list: names }) : t('cookies.providersNone')}
        </Text>
      </View>
    );
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={closeCookiePreferences}>
      <Pressable style={styles.backdrop} onPress={closeCookiePreferences}>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {/* Тап по самой карточке окно не закрывает. */}
          <Pressable
            testID="cookie-preferences"
            // CORE-1733 (возврат QA) — окно настроек cookie — поверхность v4.
            style={[styles.card, surfaceFill(isDark ? 'dark' : 'light')]}
            onPress={() => {}}
          >
            <View style={styles.header}>
              <Text style={[styles.title, { color: themeColors.textPrimary }]}>{t('cookies.settingsTitle')}</Text>
              <TouchableOpacity
                testID="cookie-preferences-close"
                accessibilityLabel={t('cookies.close')}
                onPress={closeCookiePreferences}
                hitSlop={8}
              >
                <Ionicons name="close" size={22} color={themeColors.textSecondary} />
              </TouchableOpacity>
            </View>
            <Text style={[styles.intro, { color: themeColors.textSecondary }]}>{t('cookies.settingsIntro')}</Text>

            {fixedRow('necessary')}
            {fixedRow('functional')}
            {choiceRow('analytics')}
            {choiceRow('marketing')}

            <Text style={[styles.note, { color: themeColors.textSecondary }]}>{t('cookies.reloadHint')}</Text>
            <TouchableOpacity testID="cookie-preferences-policy" onPress={openPolicy}>
              <Text style={[styles.link, { color: themeColors.textPrimary }]}>{t('cookies.policyLink')}</Text>
            </TouchableOpacity>

            <View style={styles.buttons}>
              <TouchableOpacity
                testID="cookie-preferences-save"
                style={styles.primaryButton}
                onPress={() => save(draft)}
              >
                <Text style={styles.primaryButtonText}>{t('cookies.save')}</Text>
              </TouchableOpacity>
              <View style={styles.secondaryRow}>
                <TouchableOpacity
                  testID="cookie-preferences-necessary"
                  style={[styles.secondaryButton, { borderColor: themeColors.border }]}
                  onPress={() => save(NECESSARY_COOKIES_ONLY)}
                >
                  <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>{t('cookies.necessaryOnly')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="cookie-preferences-all"
                  style={[styles.secondaryButton, { borderColor: themeColors.border }]}
                  onPress={() => save(ALL_COOKIES)}
                >
                  <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>{t('cookies.acceptAll')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </Pressable>
        </ScrollView>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1, justifyContent: 'center', padding: 16 },
  card: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    gap: 12,
    ...(Platform.OS === 'web' && { cursor: 'default' }),
  } as any,
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  title: { fontSize: 18, fontWeight: '700' },
  intro: { fontSize: 13, lineHeight: 19 },
  category: { borderWidth: 1, borderRadius: 12, padding: 14, gap: 6 },
  categoryHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  categoryTitle: { fontSize: 14, fontWeight: '700', flexShrink: 1 },
  alwaysOn: { fontSize: 12, fontWeight: '600' },
  categoryDesc: { fontSize: 13, lineHeight: 18 },
  providers: { fontSize: 12, lineHeight: 17 },
  note: { fontSize: 12, lineHeight: 17 },
  link: { fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },
  buttons: { gap: 8, marginTop: 4 },
  secondaryRow: { flexDirection: 'row', gap: 8 },
  primaryButton: {
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.orange600,
  },
  primaryButtonText: { fontSize: 14, fontWeight: '600', color: ON_ACCENT_TEXT },
  secondaryButton: { flex: 1, paddingVertical: 11, paddingHorizontal: 8, borderRadius: 10, borderWidth: 1, alignItems: 'center' },
  secondaryButtonText: { fontSize: 13, fontWeight: '600', textAlign: 'center' },
});
